import Constants from 'api-constants'
import { of } from 'rxjs'
import { map, mergeMap, catchError } from 'rxjs/operators'
import Fetch from '../fetch'
import ConfigHelper from 'framework/helpers/config'
import { Channel } from 'models'
import UserPreferencesApi from '../UserPreferencesApi'
import { remindersFactory } from '../factories'

// Tag for filtering bookmarks which is used as store for restricted channel IDs
const RESTRICTION_BY_CHANNEL = 'RESTRICTION_BY_CHANNEL'

/**
 * Deals with Channels (thanks captain obvious)
 * NAGRA STUB to be completed later
 */
export default class ChannelApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.config = config
        this.metaApi = otherApis.metaApi
        this.userPreferencesApi = otherApis.userPreferencesApi
    }

    /**
     * Sets channel as restricted
     *
     * If need to lock or unlock all channels at once just assign array of all channels to `channelsIds` parameter
     * with proper `isLock` parameter
     *
     * IMPORTANT
     *
     * Nagra has no any separate API for storing restricted channels. So the trick
     * is that we are using BookmarksApi for storing restricted channels for a user by marking
     * this bookmark as `RESTRICTION_BY_CHANNEL`
     * @param {Array<String>} platformChannelIds - channel PlatformIDs
     * @param {Boolean} isLock - define if channel should be restricted or not
     * @param {Array<String>} sourceChannelIds - Source channel IDs
     * @returns {Boolean} returns true if setting is successful
     */
    setChannelRestriction(platformChannelIds, isLock) {
        /**
         * On starhub, the channel platform id is always the Android ONE including for IOS device due to mistake done initially at project start where platformChannelId
         * It is OK for feature like favorites to use this ID specifically, we have to choice to maintain to use this ID otherwise the existing users will lose their Favorites/Information
         */
        return this.getChannelsRestriction().pipe(
            mergeMap((restrictedChannelIds = []) => {
                // if want to lock and there is no any restricted channels just assign input channel ids to restricted channel ids
                if (isLock && !restrictedChannelIds.length)
                    restrictedChannelIds = platformChannelIds

                // if want to lock and there is already some restricted channels then add only uniq values to restricted channels
                if (isLock && restrictedChannelIds.length)
                    restrictedChannelIds = Array.from(
                        new Set([...platformChannelIds, ...restrictedChannelIds])
                    )

                // if want to lock some channels so just delete input channels ids from restricted channels ids
                if (!isLock) {
                    const restrictedChannelsSet = new Set(restrictedChannelIds)
                    platformChannelIds.forEach((channelId) =>
                        restrictedChannelsSet.delete(channelId)
                    )
                    restrictedChannelIds = Array.from(restrictedChannelsSet)
                }

                return this.userPreferencesApi.addBookmark(
                    0,
                    RESTRICTION_BY_CHANNEL,
                    RESTRICTION_BY_CHANNEL,
                    // there should be only key/value pairs and API doesn't support anything except `string` in `value`
                    { restrictedChannels: restrictedChannelIds.join(';') },
                    [RESTRICTION_BY_CHANNEL]
                )
            })
        )
    }

    /**
     * Gets restricted channel IDs
     *
     * IMPORTANT
     *
     * Nagra has no any separate API for storing restricted channels. So the trick
     * is that we are using BookmarksApi for storing restricted channels for a user by marking
     * this bookmark as `RESTRICTION_BY_CHANNEL`
     * @returns {Observable<Array<String>>} returns an array of restricted channel IDs
     */
    getChannelsRestriction() {
        /**
         * On starhub, the channel platform id is always the Android ONE including for IOS device due to mistake done initially at project start where platformChannelId
         * It is OK for feature like favorites to use this ID specifically, we have to choice to maintain to use this ID otherwise the existing users will lose their Favorites/Information
         */
        return this.userPreferencesApi.getBookmarks(RESTRICTION_BY_CHANNEL).pipe(
            mergeMap((bookmarks) => {
                if (
                    !!bookmarks.length &&
                    bookmarks[0].metadata &&
                    bookmarks[0].metadata.restrictedChannels &&
                    !!bookmarks[0].metadata.restrictedChannels.length
                ) {
                    return of(bookmarks[0].metadata.restrictedChannels.split(';'))
                }
                return of([])
            }),
            catchError((error) => {
                this.logger.error('Error while fetching restricted channels. Details:', error)
                return of([])
            })
        )
    }

    /**
     * Get all channels data from Platform (auto-paginated)
     *
     * @param {Object} [opts]
     * @param {Number} [opts.pageSize=100] nb of items to fetch by page
     * @param {string} [opts.market] market of the user, specific to WOW
     * @param {string} [opts.subMarket] subMarket of the user, specific to WOW
     * @return {Observable<Array<ApiChannelData>>}
     */
    getChannelsData({ pageSize = 100, market, subMarket } = {}) {
        return of([])
    }

    /**
     * Get channels data by merging Proxy & Platform data that could be retrieved.
     * Note: Are kept only channels which are known from the Platform, otherwise they are filtered out
     * Return an Ajax Observable which emit identical returned structure from {@link ProxyApi#getChannels}
     *
     * @param {Object} [opts]
     * @param {string} [opts.tvProfile] User TV Profile (eligibility)
     * @return {Observable<ChannelsData>}
     */
    getChannels({ tvProfile = Constants.eligibility.OTT } = {}) {
        const { rollingBufferDuration = 24 } = ConfigHelper.getInstance().getConfig('nagra')
        return this.metaApi.getChannels().pipe(
            map((proxyResult) => {
                if (proxyResult && !!Object.keys(proxyResult).length) {
                    const result = Object.values(proxyResult).reduce((acc, channel) => {
                        const { platform = Constants.platform.androidTvStb } =
                            ConfigHelper.getInstance().getConfig()

                        const isSTB = platform === Constants.platform.androidTvStb
                        const isSmartTV = platform === Constants.platform.smartTvOs

                        const forceMediacorpChannelAppearanceOnStb =
                            ConfigHelper.getInstance().getConfig(
                                'mediacorp'
                            ).forceChannelAppearanceOnStb

                        let ingestChannel = false
                        if (channel.drmProvider === Channel.DRM_PROVIDER.MEDIA_CORP) {
                            ingestChannel = forceMediacorpChannelAppearanceOnStb
                                ? isSTB || isSmartTV
                                : (isSTB || isSmartTV) && channel.eligibility.includes(tvProfile)
                        } else {
                            ingestChannel = channel.eligibility.includes(tvProfile)
                        }
                        if (ingestChannel) {
                            acc[channel.id] = channel.update({
                                canWatch: true,
                                rollingBufferRetrieved: true,
                                rollingBufferPending: false,
                                isBlockedChannel: false,
                                // Not provided dynamically per channel but global configuration
                                rollingBufferDuration:
                                    channel.startOver || channel.catchup // Condition probably to refine for defining the duration
                                        ? rollingBufferDuration
                                        : 0,
                            })
                        }
                        return acc
                    }, {})
                    return result
                }

                return {}
            })
        )
    }

    /**
     * Adds reminder
     * @param {String} profileId - profile id
     * @param {String} eventId - event id
     * @param {String} channelId - channel id
     * @param {String} startTime - lower time boundary
     * @param {String} endTime - upper time boundary
     * @return {Observable<Object>} returns added reminder details
     */
    addReminder(profileId, eventId, channelId, startTime, endTime) {
        return this.userPreferencesApi.addReminder(eventId, channelId, startTime, endTime)
    }

    /**
     * Removes reminder
     * @param {String} profileId - profile id
     * @param {String} eventId - event id
     * @param {String} reminderId - reminder id
     * @return {Observable<Boolean>} returns true in case of successful removing
     */
    removeReminder(profileId, eventId, reminderId) {
        return this.userPreferencesApi.deleteReminder(reminderId)
    }

    /**
     * Gets all user's reminders
     * @param {String} profileId - profile id
     * @param {Number} startTime - lower time boundary
     * @param {number} endTime - upper time boundary
     * @return {Observable<Object>} returns object of user's reminders array
     */
    getReminderList(profileId, startTime, endTime) {
        return this.userPreferencesApi.getReminders().pipe(
            mergeMap((reminders = []) => of(remindersFactory(reminders))),
            mergeMap((reminders) => {
                const actualReminders = reminders.filter(
                    (reminder) =>
                        +reminder.availabilityStart >= +startTime &&
                        +reminder.availabilityEnd <= +endTime
                )
                return of({ reminders: actualReminders })
            })
        )
    }

    /**
     * Get RollingBuffer data for a specific channelId
     *
     * @param {String|Number} channelId
     * @return {Observable<ApiRollingBufferData>}
     */
    getRollingBuffer() {
        return of({})
    }

    /**
     * Get favourite channels of the current user
     * @return {Observable<Array<String>>} Observable which emit an Array of Platform Channel identifiers
     */
    getFavoritesChannel() {
        /**
         * On starhub, the channel platform id is always the Android ONE including for IOS device due to mistake done initially at project start where platformChannelId
         * It is OK for feature like favorites to use this ID specifically, we have to choice to maintain to use this ID otherwise the existing users will lose their Favorites/Information
         */
        if (this.config.useNagraLegacyData) {
            return this.userPreferencesApi.getBookmarks(UserPreferencesApi.FAVOURITE_CHANNELS).pipe(
                mergeMap((bookmarks) => {
                    return of(
                        bookmarks.map((bookmark) =>
                            bookmark.contentId
                                .split(UserPreferencesApi.FAVOURITE_CHANNELS_PREFIX)
                                .pop()
                        )
                    )
                })
            )
        }
        return this.userPreferencesApi.getItemsByCollectionName(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.CHANNEL
        )
    }

    /**
     * Set a channel as favorite for the current user
     * @param {String|Number} platformChannelId - channel id
     * @return {Observable<Object>} return added channel details
     */
    setFavoriteChannel(platformChannelId) {
        /**
         * On starhub, the channel platform id is always the Android ONE including for IOS device due to mistake done initially at project start where platformChannelId
         * It is OK for feature like favorites to use this ID specifically, we have to choice to maintain to use this ID otherwise the existing users will lose their Favorites/Information
         */
        if (this.config.useNagraLegacyData) {
            return this.userPreferencesApi.addBookmark(
                0, //position
                `${UserPreferencesApi.FAVOURITE_CHANNELS_PREFIX}${platformChannelId}`,
                UserPreferencesApi.FAVOURITE_CHANNELS,
                { type: 'NagraChannel' },
                [UserPreferencesApi.FAVOURITE_CHANNELS]
            )
        }
        return this.userPreferencesApi.addCollectionItem(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.CHANNEL,
            platformChannelId
        )
    }

    /**
     * Remove a channel as favorite for the current user
     * @param {String|Number} platformChannelId
     * @return {Observable<Boolean>} returns true in case of successful removing
     */
    removeFavoriteChannel(platformChannelId) {
        /**
         * On starhub, the channel platform id is always the Android ONE including for IOS device due to mistake done initially at project start where platformChannelId
         * It is OK for feature like favorites to use this ID specifically, we have to choice to maintain to use this ID otherwise the existing users will lose their Favorites/Information
         */
        if (this.config.useNagraLegacyData) {
            return this.userPreferencesApi.deleteBookmark(
                `${UserPreferencesApi.FAVOURITE_CHANNELS_PREFIX}${platformChannelId}`
            )
        }
        return this.userPreferencesApi.deleteCollectionItem(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.CHANNEL,
            platformChannelId
        )
    }

    /**
     * Retrieve currently broadcasted Live programs from metaApi
     *
     * @deprecated Could not works with available parameters on Maculosa startTimeStamp and endTimeStamp refers to the "begin" (ie broadcast time) and act as boundaries
     *
     * @param {Number} channelId Proxy channel identifier
     * @return {Observable} Observable, see return of {@link ProxyApi#getChannelsData}
     */
    getOnTvNow() {
        return of({})
    }
}
